<script setup lang="ts">
/// <reference types="@types/google.maps" />
// Import custom Google Maps types

import type { PlaceDetail } from '@consumer/types/serializers'
import locationDot from '@consumer/assets/icons/v6/location-dot-light.svg'
import closeIcon from '@consumer/assets/icons/close.svg'
import merchantLogo from '@consumer/assets/icons/v6/merchant.svg'
import close2Icon from '@consumer/assets/icons/close.svg?svgicon'
import newGlobeIcon from '@consumer/assets/icons/new/globe.svg?svgicon'

withDefaults(defineProps<{
  places: PlaceDetail[]
}>(), {  })

const mapRef = ref()

function middle (nums: number[]) {
  if (nums.length > 1) {
    return (nums[0] + nums[nums.length - 1]) / 2
  }
  else if (nums.length === 1) {
    return nums[0]
  }

  return 0
}

function sortNums (strings: string[]): number[] {
  return strings
    .map(a => parseFloat(a))
    .sort((a, b) => a - b)
}

const placesWithLocation = computed(() => __props.places.filter(place => place.lat && place.lon) ?? [])

const lats = computed(() => sortNums(placesWithLocation.value.map(place => place.lat ?? '')))
const longs = computed(() => sortNums(placesWithLocation.value.map(place => place.lon ?? '')))

const nationwidePlaces = computed(() =>
  __props.places.filter(place => place.isNationwide && place.imageUrl != null) ?? [])

const center = computed(() => {
  return {
    lat: middle(lats.value),
    lng: middle(longs.value),
  }
})

function placeToInfoPanel (place: PlaceDetail, addCloseButton: boolean = false) {
  const div = document.createElement('div')

  if (addCloseButton) {
    const closeButton = document.createElement('div')
    closeButton.className = 'close-button'
    const img = document.createElement('img')
    img.src = closeIcon
    closeButton.appendChild(img)
    div.appendChild(closeButton)
  }

  div.className = 'flex flex-row gap-3 items-center'
  if (place.imageUrl) {
    const img = document.createElement('img')
    img.className = 'w-[80px] rounded-v2md'
    img.src = place.imageUrl
    img.onerror = () => { (img as HTMLImageElement).src = merchantLogo }
    div.appendChild(img)
  }

  const namePanel = document.createElement('div')
  namePanel.className = 'flex flex-col gap-1'
  div.appendChild(namePanel)

  const name = document.createElement('div')
  name.className = 'place-name text-sm font-semibold !text-black hover:underline clickable'
  name.onclick = function () {
    const el = document.querySelector(`[data-placeid="${place.giftlyId}"]`)
    el?.scrollIntoView({ behavior: 'smooth' })
  }
  name.appendChild(document.createTextNode(place.name ?? ''))
  namePanel.appendChild(name)

  if (place.categories && place.categories.length > 0) {
    const category = document.createElement('div')
    category.className = 'text-12px !text-v2-tertiary'
    category.appendChild(document.createTextNode(place.categories[0]))
    namePanel.appendChild(category)
  }

  return div
}

function jumpToPlace (place: PlaceDetail) {
  const el = document.querySelector(`[data-placeid="${place.giftlyId}"]`)
  el?.scrollIntoView({ behavior: 'smooth' })
}
watch(__props.places, async () => {
  await gPlaceMap?.updatePlacesOnMap()
})

const nationwideTemplate = ref()

class GPlaceMap {
  markers: google.maps.marker.AdvancedMarkerElement[] = []
  currentMarker: google.maps.marker.AdvancedMarkerElement | null = null
  currentPlace: PlaceDetail | null = null
  infoWindow: google.maps.InfoWindow | null = null
  map: google.maps.Map

  constructor (map: google.maps.Map) {
    this.map = map
    google.maps.importLibrary('maps').then((library: any) => {
      const { InfoWindow } = library as google.maps.MapsLibrary

      this.markers = []
      this.infoWindow = new InfoWindow({ maxWidth: 250 })
      this.setupInfoWindow()
      this.setupBounds()
      this.addPlacesToMap()
      this.setupNationwide()
    })
  }

  setupInfoWindow () {
    if (this.infoWindow) {
      this.infoWindow.addListener('closeclick', () => {
        if (this.currentPlace?.imageUrl && this.currentMarker) {
          const img = document.createElement('img')
          img.src = this.currentPlace.imageUrl
          img.className = 'place-icon'
          img.onerror = () => { (img as HTMLImageElement).src = merchantLogo }
          this.currentMarker.content = img
        }
      })
    }
  }

  setupBounds () {
    const bounds = new google.maps.LatLngBounds()
    placesWithLocation.value.forEach((place) => {
      bounds.extend(new google.maps.LatLng(parseFloat(place.lat ?? '0'), parseFloat(place.lon ?? '0')))
    })
    if (this.map) {
      this.map.fitBounds(bounds)

      const zoom = this.map.getZoom()
      if (zoom && zoom > 11) {
        this.map.setZoom(11)
      }
    }
  }

  setupNationwide () {
    if (nationwidePlaces.value.length > 0 && this.map) {
      // Add Nationwide panel
      this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(nationwideTemplate.value)
      setTimeout(() => {
        // Wait for map to load
        nationwideTemplate.value.style.display = 'flex'
      }, 700)
    }
  }

  async updatePlacesOnMap () {
    this.markers.forEach((marker) => {
      marker.map = null
    })
    this.markers = []
    await this.addPlacesToMap()
  }

  async addPlacesToMap () {
    const markerLibrary = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary
    const { AdvancedMarkerElement } = markerLibrary

    this.markers = placesWithLocation.value.map((place) => {
      const img = document.createElement('img')
      if (place.imageUrl) {
        img.src = place.imageUrl
        img.className = 'place-icon'
        img.onerror = () => { img.src = merchantLogo }
      }

      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: new google.maps.LatLng(parseFloat(place.lat ?? '0'), parseFloat(place.lon ?? '0')),
        content: img,
        title: place.name,
        gmpClickable: true,
      })

      marker.addListener('gmp-click', () => {
        this.infoWindow?.close()

        if (this.currentMarker != null && this.currentPlace != null && this.currentPlace.imageUrl) {
          const img = document.createElement('img')
          img.src = this.currentPlace.imageUrl
          img.className = 'place-icon'
          this.currentMarker.content = img
        }

        this.currentMarker = marker
        this.currentPlace = place
        const locationDotEl = document.createElement('img')
        locationDotEl.className = 'place-icon location-dot'
        locationDotEl.src = locationDot
        marker.content = locationDotEl

        this.infoWindow?.setContent(placeToInfoPanel(place))
        this.infoWindow?.open({
          map: this.map,
          anchor: marker,
        })
      })
      return marker
    })
  }
}

let gPlaceMap: GPlaceMap

watch(mapRef, async (googleMap) => {
  if (googleMap && googleMap.$mapPromise) {
    googleMap.$mapPromise.then(async (map: google.maps.Map) => {
      gPlaceMap = new GPlaceMap(map)
    })
  }
})

const iconOffset = 25
</script>
<template>
  <div
    ref="nationwideTemplate"
    style="display: none;"
    class="flex flex-row gap-1 bg-white rounded-v2lg pr-2 text-xl text-grey-900 gap-4 font-weight-400 map-control"
  >
    <div
      class="relative"
      :style="{ width: nationwidePlaces.length * (4 + iconOffset) + 'px'}"
    >
      <GTooltip
        v-for="(place, idx) in nationwidePlaces"
        :key="place.giftlyId"
        :triggers="['click']"
        autoHide
        popperClass="map-nationwide-popper"
      >
        <img
          class="place-icon rounded-full clickable absolute"
          :src="place.imageUrl ?? merchantLogo"
          :style="{left: `${idx * iconOffset}px`}"
          @error="(e) => { (e.target as HTMLImageElement).src = merchantLogo }"
        >

        <template #popper>
          <div class="flex flex-row gap-3 items-center">
            <div v-close-popper class="close-button">
              <GIcon :svg="close2Icon" color="black"/>
            </div>
            <div v-if="place.imageUrl" class="w-[80px] rounded-v2md">
              <img :src="place.imageUrl">
            </div>
            <div class="flex flex-col gap-1">
              <div
                class="place-name text-sm font-semibold !text-black hover:underline clickable"
                @click="jumpToPlace(place)"
              >
                {{ place.name }}
              </div>
              <div
                v-if="place.categories?.length"
                class="text-12px !text-v2-tertiary"
              >
                {{ place.categories[0] }}
              </div>
            </div>
          </div>
        </template>
      </GTooltip>
    </div>
    <div class="flex flex-row items-center gap-1">
      <GIcon :svg="newGlobeIcon" class="globe-icon" size="20px"/>
      Nationwide
    </div>
  </div>
  <GMapMap
    ref="mapRef"
    :center="center"
    :zoom="11"
    mapTypeId="roadmap"
    style="border-radius: 12px; height: 400px; width: 100%;"
    :options="{
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      mapId: 'multiplaceGiftsMap'
    }"
  />
</template>
<style lang="scss">
.place-icon {
  background-color: #fff;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2.076px 2.076px 0 rgb(0 0 0 / 6%), 3.238px 3.238px 12.95px 0 rgb(0 0 0 / 10%);
  height: 40px;
  width: 40px;
}

.location-dot {
  padding: 6px;
}

.map-control {
  height: 42px;
}

.vue-map-container {
  .gm-style-iw-c {
    overflow: visible !important;
    padding: 12px !important;
  }

  .gm-style-iw-chr {
    position: absolute;
    right: -10px;
    top: -10px;
  }

  .gm-style-iw-chr button {
    background-color: #fff !important;
    border-radius: 50% !important;
    box-shadow: 0 2.076px 2.076px 0 rgb(0 0 0 / 6%), 3.238px 3.238px 12.95px 0 rgb(0 0 0 / 10%);
    height: 24px !important;
    opacity: 1 !important;
    padding: 4px !important;
    width: 24px !important;
  }

  .gm-style-iw-chr button span {
    margin: -4px 0 0 -4px !important;
  }
}

.map-nationwide-popper {
  position: relative;
}

.map-nationwide-popper .v-popper__inner {
  background-color: #fff;
  overflow: visible;
}

.map-nationwide-popper .v-popper__wrapper .v-popper__arrow-container .v-popper__arrow-outer {
  border-top-color: #fff;
}

.map-nationwide-popper .close-button {
  background-color: #fff;
  border-radius: 50%;
  box-shadow: 0 2.076px 2.076px 0 rgb(0 0 0 / 6%), 3.238px 3.238px 12.95px 0 rgb(0 0 0 / 10%);
  cursor: pointer;
  height: 24px;
  padding: 4px;
  position: absolute;
  right: -15px;
  top: -15px;
  width: 24px;
}
</style>
